/**
 * Dónde atiende la institución y con qué equipos.
 *
 * POR QUE IMPORTA LO QUE SE VE AQUI
 * =================================
 * Es lo que determina cuántos pacientes caben a la misma hora: no lo decide la
 * institución, lo deciden las máquinas que hay en cada sede. Una IPS con dos
 * ecógrafos puede atender dos ecografías a las diez; con uno, no.
 *
 * LA MAYORIA TIENE UNA SOLA SEDE
 * ==============================
 * Y a esa no hay que explicarle una jerarquía. La pantalla lo sabe: con una
 * sola, ni siquiera dice la palabra "sede" en el encabezado, y se presenta
 * como lo que le interesa a esa IPS, que son sus equipos.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Loader2, MapPin, Monitor, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import {
  crearEquipo,
  crearSede,
  darDeBajaEquipo,
  darDeBajaSede,
  getEquipos,
  getSedes,
  type Sede,
} from '@/application/sedes/sedes.service'
import { toast } from '@/lib/toast'

/** Cómo llama un radiólogo a cada modalidad. */
const NOMBRE_DE_LA_MODALIDAD: Record<string, string> = {
  CT: 'Tomografía',
  MR: 'Resonancia',
  US: 'Ecografía',
  DX: 'Radiografía',
  CR: 'Radiografía',
  MG: 'Mamografía',
  XA: 'Angiografía',
  NM: 'Medicina nuclear',
  PT: 'PET',
  RF: 'Fluoroscopia',
  ES: 'Endoscopia',
  OT: 'Otro',
}

export function SedesPage() {
  const queryClient = useQueryClient()
  const [nuevaSede, setNuevaSede] = useState('')
  const [nuevoEquipo, setNuevoEquipo] = useState<{ sedeId: string; nombre: string; modalidad: string } | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['sedes'], queryFn: getSedes })
  const { data: equipos } = useQuery({ queryKey: ['equipos'], queryFn: () => getEquipos() })

  const recargar = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sedes'] }),
      queryClient.invalidateQueries({ queryKey: ['equipos'] }),
    ])

  const agregarSede = useMutation({
    mutationFn: (nombre: string) => crearSede({ nombre }),
    onSuccess: async () => {
      setNuevaSede('')
      await recargar()
      toast.success('Sede agregada')
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'No se pudo agregar la sede'),
  })

  const quitarSede = useMutation({
    mutationFn: (id: string) => darDeBajaSede(id),
    onSuccess: async () => {
      await recargar()
      toast.success('Sede dada de baja')
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'No se pudo dar de baja la sede'),
  })

  const agregarEquipo = useMutation({
    mutationFn: (datos: { sedeId: string; nombre: string; modalidad: string }) =>
      crearEquipo(datos),
    onSuccess: async () => {
      setNuevoEquipo(null)
      await recargar()
      toast.success('Equipo registrado')
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'No se pudo registrar el equipo'),
  })

  const quitarEquipo = useMutation({
    mutationFn: (id: string) => darDeBajaEquipo(id),
    onSuccess: async () => {
      await recargar()
      toast.success('Equipo dado de baja')
    },
  })

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center gap-3 py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
        <span className="text-[var(--foreground-muted)]">Cargando...</span>
      </div>
    )
  }

  const equiposDe = (sede: Sede) =>
    (equipos?.equipos ?? []).filter((e) => e.sedeId === sede.id)

  return (
    <div className="space-y-6">
      <div>
        {/* Con una sola sede ni se nombra el concepto: a esa IPS lo que le
            interesa son sus equipos, no una jerarquía que no tiene. */}
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          {data.variasSedes ? (
            <Building2 className="h-6 w-6 text-[var(--primary)]" />
          ) : (
            <Monitor className="h-6 w-6 text-[var(--primary)]" />
          )}
          {data.variasSedes ? 'Sedes y equipos' : 'Equipos'}
        </h1>
        <p className="mt-1 text-[var(--foreground-muted)]">
          {data.variasSedes
            ? 'Dónde atiende su institución y con qué equipos cuenta cada sede.'
            : 'Con qué equipos cuenta su institución. Son los que determinan cuántos pacientes caben a la misma hora.'}
        </p>
      </div>

      {data.sedes.map((sede) => (
        <Card key={sede.id}>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[var(--primary)]" />
                {sede.nombre}
                {sede.esPrincipal && data.variasSedes && <Badge>Principal</Badge>}
              </CardTitle>

              {/* La principal no se da de baja: es la que recibe todo lo que
                  no indica sede. Ni se ofrece el botón. */}
              {!sede.esPrincipal && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => quitarSede.mutate(sede.id)}
                  disabled={quitarSede.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Dar de baja
                </Button>
              )}
            </div>
            {sede.ciudad && (
              <p className="mt-1 text-sm text-[var(--foreground-muted)]">{sede.ciudad}</p>
            )}
          </CardHeader>

          <CardContent className="space-y-3">
            {equiposDe(sede).length === 0 && (
              <p className="text-sm text-[var(--foreground-muted)]">
                Todavía no hay equipos registrados aquí.
              </p>
            )}

            {equiposDe(sede).map((equipo) => (
              <div
                key={equipo.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--border)] p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">{equipo.nombre}</p>
                  <p className="text-sm text-[var(--foreground-muted)]">
                    {NOMBRE_DE_LA_MODALIDAD[equipo.modalidad] ?? equipo.modalidad}
                    {equipo.sala ? ` · ${equipo.sala}` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => quitarEquipo.mutate(equipo.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {nuevoEquipo?.sedeId === sede.id ? (
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-[var(--primary)] p-3">
                <div className="min-w-[180px] flex-1">
                  <label className="text-sm" htmlFor={`equipo-${sede.id}`}>
                    Nombre del equipo
                  </label>
                  <Input
                    id={`equipo-${sede.id}`}
                    autoFocus
                    value={nuevoEquipo.nombre}
                    placeholder="Ecógrafo 1"
                    onChange={(e) =>
                      setNuevoEquipo({ ...nuevoEquipo, nombre: e.target.value })
                    }
                  />
                </div>
                <div className="min-w-[160px]">
                  <label className="text-sm" htmlFor={`modalidad-${sede.id}`}>
                    Qué hace
                  </label>
                  <select
                    id={`modalidad-${sede.id}`}
                    className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                    value={nuevoEquipo.modalidad}
                    onChange={(e) =>
                      setNuevoEquipo({ ...nuevoEquipo, modalidad: e.target.value })
                    }
                  >
                    {(equipos?.modalidades ?? []).map((m) => (
                      <option key={m} value={m}>
                        {NOMBRE_DE_LA_MODALIDAD[m] ?? m}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  size="sm"
                  disabled={!nuevoEquipo.nombre.trim() || agregarEquipo.isPending}
                  onClick={() => agregarEquipo.mutate(nuevoEquipo)}
                >
                  Registrar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setNuevoEquipo(null)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setNuevoEquipo({
                    sedeId: sede.id,
                    nombre: '',
                    modalidad: equipos?.modalidades?.[0] ?? 'US',
                  })
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Agregar equipo
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-2 py-5">
          <div className="min-w-[220px] flex-1">
            <label className="text-sm" htmlFor="nueva-sede">
              {data.variasSedes ? 'Agregar otra sede' : '¿Atiende en más de un lugar?'}
            </label>
            <Input
              id="nueva-sede"
              value={nuevaSede}
              placeholder="Sede Norte"
              onChange={(e) => setNuevaSede(e.target.value)}
            />
          </div>
          <Button
            disabled={!nuevaSede.trim() || agregarSede.isPending}
            onClick={() => agregarSede.mutate(nuevaSede)}
          >
            {agregarSede.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Agregar sede
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
